const GOOGLE_FONTS_BASE = 'https://fonts.googleapis.com/css2'
const FONTSHARE_CSS_BASE = 'https://api.fontshare.com/v2/css'
const VELVETYNE_BASE = `${process.env.BASE_URL || '/'}fonts/velvetyne/`

const FONT_FILE_FORMATS = {
  woff2: 'woff2',
  woff: 'woff',
  ttf: 'truetype',
  otf: 'opentype',
}

export const quoteFamily = (family) => `"${String(family).replace(/"/g, '\\"')}"`

const googleHref = (family, weights = [400]) => {
  const axis = Array.from(new Set(weights))
    .sort((a, b) => a - b)
    .join(';')
  const familyParam = encodeURIComponent(family).replace(/%20/g, '+')
  return `${GOOGLE_FONTS_BASE}?family=${familyParam}:wght@${axis}&display=swap`
}

const fontshareHref = (slug, weights = [400]) => {
  const axis = Array.from(new Set(weights))
    .sort((a, b) => a - b)
    .join(',')
  return `${FONTSHARE_CSS_BASE}?f[]=${encodeURIComponent(slug)}@${axis}&display=swap`
}

const buildFace = (family, files = [], weight = 400, style = 'normal') => ({
  family,
  weight,
  style,
  sources: files.map((file) => ({
    url: `${VELVETYNE_BASE}${file}`,
    format: FONT_FILE_FORMATS[file.split('.').pop()] || 'woff2',
  })),
})

const localEntry = ({
  id,
  family,
  label,
  weights = [400],
  styles = ['normal'],
  fallback = `'Helvetica Neue', Helvetica, Arial, sans-serif`,
  defaultWeight = null,
}) => ({
  id,
  family,
  label,
  provider: 'Local',
  sourceType: 'local',
  weights,
  styles,
  fallback,
  defaultWeight: defaultWeight || weights[0] || 400,
})

const stylesheetEntry = ({
  id,
  family,
  label,
  provider,
  href,
  weights = [400],
  styles = ['normal'],
  fallback = `'Helvetica Neue', Helvetica, Arial, sans-serif`,
  defaultWeight = null,
}) => ({
  id,
  family,
  label,
  provider,
  sourceType: 'stylesheet',
  href,
  weights,
  styles,
  fallback,
  defaultWeight: defaultWeight || weights[0] || 400,
})

const faceEntry = ({
  id,
  family,
  label,
  provider = 'Velvetyne',
  faces,
  fallback = `'Helvetica Neue', Helvetica, Arial, sans-serif`,
}) => ({
  id,
  family,
  label,
  provider,
  sourceType: 'faces',
  faces,
  weights: Array.from(new Set((faces || []).map((face) => Number(face.weight) || 400))).sort((a, b) => a - b),
  styles: Array.from(new Set((faces || []).map((face) => face.style || 'normal'))),
  fallback,
  defaultWeight: Number(faces?.[0]?.weight) || 400,
})

const LOCAL_TITLE_FONTS = [
  localEntry({
    id: 'system-sans',
    family: 'Helvetica Neue',
    label: 'Local / System Sans',
    weights: [300, 400, 500, 700],
    styles: ['normal', 'italic'],
  }),
  localEntry({
    id: 'pragmatica-extended',
    family: 'Pragmatica Extended',
    label: 'Local / Pragmatica Extended',
    weights: [700],
    styles: ['normal', 'italic'],
    defaultWeight: 700,
  }),
  localEntry({
    id: 'cherry-bomb',
    family: 'Cherry Bomb',
    label: 'Local / Cherry Bomb',
    weights: [400],
  }),
]

const GOOGLE_TITLE_FONTS = [
  stylesheetEntry({
    id: 'bitcount-grid-single-ink',
    family: 'Bitcount Grid Single Ink',
    label: 'Google / Bitcount Grid Single Ink',
    provider: 'Google Fonts',
    href: googleHref('Bitcount Grid Single Ink', [100, 200, 300, 400]),
    weights: [100, 200, 300, 400],
    defaultWeight: 400,
  }),
  stylesheetEntry({
    id: 'rubik-glitch',
    family: 'Rubik Glitch',
    label: 'Google / Rubik Glitch',
    provider: 'Google Fonts',
    href: googleHref('Rubik Glitch', [400]),
  }),
  stylesheetEntry({
    id: 'rubik-iso',
    family: 'Rubik Iso',
    label: 'Google / Rubik Iso',
    provider: 'Google Fonts',
    href: googleHref('Rubik Iso', [400]),
  }),
  stylesheetEntry({
    id: 'ranchers',
    family: 'Ranchers',
    label: 'Google / Ranchers',
    provider: 'Google Fonts',
    href: googleHref('Ranchers', [400]),
  }),
  stylesheetEntry({
    id: 'fascinate',
    family: 'Fascinate',
    label: 'Google / Fascinate',
    provider: 'Google Fonts',
    href: googleHref('Fascinate', [400]),
  }),
  stylesheetEntry({
    id: 'imperial-script',
    family: 'Imperial Script',
    label: 'Google / Imperial Script',
    provider: 'Google Fonts',
    href: googleHref('Imperial Script', [400]),
  }),
  stylesheetEntry({
    id: 'fascinate-inline',
    family: 'Fascinate Inline',
    label: 'Google / Fascinate Inline',
    provider: 'Google Fonts',
    href: googleHref('Fascinate Inline', [400]),
  }),
  stylesheetEntry({
    id: 'splash',
    family: 'Splash',
    label: 'Google / Splash',
    provider: 'Google Fonts',
    href: googleHref('Splash', [400]),
  }),
  stylesheetEntry({
    id: 'monoton',
    family: 'Monoton',
    label: 'Google / Monoton',
    provider: 'Google Fonts',
    href: googleHref('Monoton', [400]),
  }),
  stylesheetEntry({
    id: 'tilt-prism',
    family: 'Tilt Prism',
    label: 'Google / Tilt Prism',
    provider: 'Google Fonts',
    href: googleHref('Tilt Prism', [400]),
  }),
  stylesheetEntry({
    id: 'neonderthaw',
    family: 'Neonderthaw',
    label: 'Google / Neonderthaw',
    provider: 'Google Fonts',
    href: googleHref('Neonderthaw', [400]),
  }),
  stylesheetEntry({
    id: 'silkscreen',
    family: 'Silkscreen',
    label: 'Google / Silkscreen',
    provider: 'Google Fonts',
    href: googleHref('Silkscreen', [400]),
  }),
  stylesheetEntry({
    id: 'ewert',
    family: 'Ewert',
    label: 'Google / Ewert',
    provider: 'Google Fonts',
    href: googleHref('Ewert', [400]),
  }),
  stylesheetEntry({
    id: 'honk',
    family: 'Honk',
    label: 'Google / Honk',
    provider: 'Google Fonts',
    href: googleHref('Honk', [400]),
  }),
  stylesheetEntry({
    id: 'danfo',
    family: 'Danfo',
    label: 'Google / Danfo',
    provider: 'Google Fonts',
    href: googleHref('Danfo', [400]),
  }),
  stylesheetEntry({
    id: 'bungee-outline',
    family: 'Bungee Outline',
    label: 'Google / Bungee Outline',
    provider: 'Google Fonts',
    href: googleHref('Bungee Outline', [400]),
  }),
  stylesheetEntry({
    id: 'bungee-shade',
    family: 'Bungee Shade',
    label: 'Google / Bungee Shade',
    provider: 'Google Fonts',
    href: googleHref('Bungee Shade', [400]),
  }),
  stylesheetEntry({
    id: 'rubik-vinyl',
    family: 'Rubik Vinyl',
    label: 'Google / Rubik Vinyl',
    provider: 'Google Fonts',
    href: googleHref('Rubik Vinyl', [400]),
  }),
  stylesheetEntry({
    id: 'indie-flower',
    family: 'Indie Flower',
    label: 'Google / Indie Flower',
    provider: 'Google Fonts',
    href: googleHref('Indie Flower', [400]),
  }),
]

const FONTSHARE_TITLE_FONTS = [
  ['comico', 'Comico', [400]],
  ['britney', 'Britney', [400, 700]],
  ['array', 'Array', [400, 700]],
  ['styro', 'Styro', [400, 500, 700]],
  ['melodrama', 'Melodrama', [400, 500, 700]],
  ['zina', 'Zina', [400]],
  ['segment', 'Segment', [400]],
  ['teko', 'Teko', [400, 500, 700]],
  ['nunito', 'Nunito', [400, 500, 700]],
].map(([slug, family, weights]) => stylesheetEntry({
  id: `fontshare-${slug}`,
  family,
  label: `Fontshare / ${family}`,
  provider: 'Fontshare',
  href: fontshareHref(slug, weights),
  weights,
}))

const VELVETYNE_TITLE_FONTS = [
  ['velvetyne-flor-germen', 'florderuina-germen', 'Flor de Ruina Germen', ['florderuina-germen.woff2', 'florderuina-germen.woff']],
  ['velvetyne-flor-flor', 'florderuina-flor', 'Flor de Ruina Flor', ['florderuina-flor.woff2', 'florderuina-flor.woff']],
  ['velvetyne-flor-fractura', 'florderuina-fractura', 'Flor de Ruina Fractura', ['florderuina-fractura.woff2', 'florderuina-fractura.woff']],
  ['velvetyne-jgs-font', 'jgs_font', 'Jgs Font', ['jgs_font.ttf']],
  ['velvetyne-jgs5', 'jgs5', 'Jgs 5', ['jgs5.woff2', 'jgs5.woff']],
  ['velvetyne-jgs7', 'jgs7', 'Jgs 7', ['jgs7.woff2', 'jgs7.woff']],
  ['velvetyne-lithops', 'lithops-regular', 'Lithops', ['lithops-regular.woff2', 'lithops-regular.woff']],
  ['velvetyne-anthony', 'anthony', 'Anthony', ['anthony.woff2', 'anthony.woff']],
  ['velvetyne-picnic', 'picnic-regular', 'PicNic', ['picnic-regular.woff2', 'picnic-regular.woff']],
  ['velvetyne-cirruscumulus', 'cirruscumulus', 'CirrusCumulus', ['cirruscumulus.woff', 'cirruscumulus.ttf']],
  ['velvetyne-kaeru-kaeru', 'kaerukaeru-regular', 'Kaeru Kaeru', ['kaerukaeru-regular.woff2', 'kaerukaeru-regular.woff']],
  ['velvetyne-ouroboros', 'ouroboros-regular', 'Ouroboros', ['ouroboros-regular.woff2', 'ouroboros-regular.woff']],
  ['velvetyne-backout', 'backout', 'BackOut', ['backout.woff2', 'backout.woff']],
  ['velvetyne-hyper-scrypt', 'hyperscrypt-stencil', 'Hyper Scrypt', ['hyperscrypt-stencil.woff2', 'hyperscrypt-stencil.woff']],
].map(([id, family, name, files]) => faceEntry({
  id,
  family,
  label: `Velvetyne / ${name}`,
  faces: [buildFace(family, files)],
}))

export const DEFAULT_TITLE_FONT_ID = 'pragmatica-extended'
export const FALLBACK_TITLE_FONT_ID = 'system-sans'

export const TITLE_FONT_LIBRARY = [
  ...LOCAL_TITLE_FONTS,
  ...GOOGLE_TITLE_FONTS,
  ...FONTSHARE_TITLE_FONTS,
  ...VELVETYNE_TITLE_FONTS,
]

export const TITLE_FONT_LIBRARY_BY_ID = Object.freeze(
  TITLE_FONT_LIBRARY.reduce((acc, entry) => {
    acc[entry.id] = entry
    return acc
  }, {})
)

export const TITLE_FONT_OPTIONS = TITLE_FONT_LIBRARY.map((entry) => ({
  label: entry.label,
  value: entry.id,
}))

export const TITLE_FONT_WEIGHT_OPTIONS = [
  { label: '100', value: 100 },
  { label: '200', value: 200 },
  { label: '300', value: 300 },
  { label: '400', value: 400 },
  { label: '500', value: 500 },
  { label: '600', value: 600 },
  { label: '700', value: 700 },
  { label: '800', value: 800 },
  { label: '900', value: 900 },
]

export const TITLE_FONT_STYLE_OPTIONS = [
  { label: 'Normal', value: 'normal' },
  { label: 'Italic', value: 'italic' },
]

export const TITLE_TEXT_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

export function getTitleFontEntry(fontId) {
  return TITLE_FONT_LIBRARY_BY_ID[fontId] || TITLE_FONT_LIBRARY_BY_ID[DEFAULT_TITLE_FONT_ID] || TITLE_FONT_LIBRARY[0] || null
}

export function resolveTitleFontWeight(entry, requestedWeight) {
  const weights = Array.isArray(entry?.weights) && entry.weights.length
    ? entry.weights.map((value) => Number(value) || 400)
    : [400]
  const requested = Number(requestedWeight) || Number(entry?.defaultWeight) || weights[0] || 400
  return weights.reduce((nearest, candidate) => {
    return Math.abs(candidate - requested) < Math.abs(nearest - requested)
      ? candidate
      : nearest
  }, weights[0])
}

export function resolveTitleFontStyle(entry, requestedStyle) {
  const styles = Array.isArray(entry?.styles) && entry.styles.length
    ? entry.styles
    : ['normal']
  if (styles.includes(requestedStyle)) return requestedStyle
  return styles[0] || 'normal'
}

export function resolveTitleFontSelection(fontId, requestedWeight = null, requestedStyle = 'normal') {
  const entry = getTitleFontEntry(fontId)
  if (!entry) {
    return {
      id: FALLBACK_TITLE_FONT_ID,
      family: 'Helvetica Neue',
      fontStack: `${quoteFamily('Helvetica Neue')}, Helvetica, Arial, sans-serif`,
      weight: 400,
      style: 'normal',
      provider: 'Local',
    }
  }
  const weight = resolveTitleFontWeight(entry, requestedWeight)
  const style = resolveTitleFontStyle(entry, requestedStyle)
  return {
    ...entry,
    weight,
    style,
    fontStack: `${quoteFamily(entry.family)}, ${entry.fallback || `'Helvetica Neue', Helvetica, Arial, sans-serif`}`,
  }
}

export function buildTitleFontFaceCss(entry) {
  if (!entry || entry.sourceType !== 'faces') return ''
  return (entry.faces || [])
    .map((face) => {
      const sources = (face.sources || []).map(
        (source) => `url('${source.url}') format('${source.format}')`
      )
      return `@font-face {
  font-family: ${quoteFamily(face.family)};
  src: ${sources.join(',\n       ')};
  font-weight: ${face.weight || 400};
  font-style: ${face.style || 'normal'};
  font-display: swap;
}`
    })
    .join('\n')
}

